import React, { useEffect, useState } from "react";
import { FileSignature, RotateCcw } from "lucide-react";
import {
  AppButton, AppModal, Callout, FormField, Input, NativeSelect, Textarea,
} from "../../ui";
import apiClient from "../../../utils/apiClient";
import {
  STATE_OPTIONS, gstinLooksRight, gstinProblem, normaliseGstin, stateLabel, stateOf,
} from "../../../utils/feeInvoice";

const LIMITS = { name: 120, address: 300 };

const toForm = (billTo) => ({
  name: billTo?.name || "",
  address: billTo?.address || "",
  gstin: billTo?.gstin || "",
  stateCode: billTo?.stateCode || "",
});

/**
 * Who ONE invoice is made out to — a company paying for its employee (its name,
 * address and GSTIN, so it can claim the input tax), a parent, or a student in
 * another state (the place of supply turns CGST + SGST into IGST).
 *
 * Changes what the invoice prints and nothing else: not the payment, not the
 * student's record. Saving with every field empty makes it the student's again.
 *
 * The GST fields show only on a payment GST applies to — elsewhere they would
 * print nothing.
 */
export default function BillingDetailsModal({ show, onHide, feeId, billTo, student, gst, onSaved }) {
  const [form, setForm] = useState(toForm(billTo));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (show) {
      setForm(toForm(billTo));
      setError("");
    }
  }, [show, billTo]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const gstin = normaliseGstin(form.gstin);
  const gstinError = gstinProblem(gstin);
  const gstinWarning = gstin && !gstinError && !gstinLooksRight(gstin);
  const supplierState = gst?.supplierState || null;
  const impliedState = (gstin && !gstinError ? stateOf(gstin.slice(0, 2)) : null) || supplierState;
  const chosenState = stateOf(form.stateCode) || impliedState;
  const interState = Boolean(supplierState && chosenState && supplierState.code !== chosenState.code);

  const save = async (body) => {
    setSaving(true);
    setError("");
    try {
      const { data } = await apiClient.put(`/fees/${feeId}/bill-to`, body);
      onSaved?.(data);
    } catch (e) {
      setError(e?.response?.data?.message || "Couldn't save the billing details. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const submit = (e) => {
    e?.preventDefault();
    if (gstinError) return;
    save({ ...form, gstin });
  };

  return (
    <AppModal
      show={show}
      onHide={() => { if (!saving) onHide(); }}
      title="Billing details for this invoice"
      icon={<FileSignature size={18} />}
      footer={
        <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
          <div>
            {billTo && (
              <AppButton variant="ghost" onClick={() => save({})} disabled={saving}>
                <RotateCcw size={14} /> Use the student&apos;s details
              </AppButton>
            )}
          </div>
          <div className="tw-flex tw-gap-2">
            <AppButton variant="secondary" onClick={onHide} disabled={saving}>Cancel</AppButton>
            <AppButton onClick={submit} disabled={saving || Boolean(gstinError)}>
              {saving ? "Saving…" : "Save"}
            </AppButton>
          </div>
        </div>
      }
    >
      <form className="tw-flex tw-flex-col tw-gap-4" onSubmit={submit}>
        <p className="tw-m-0 tw-text-sm tw-text-content-muted">
          Changes what this invoice says — not the payment, and not {student?.name || "the student"}&apos;s record.
          Leave a field empty to use the student&apos;s own.
        </p>
        {error && <Callout tone="danger" role="alert">{error}</Callout>}

        <FormField label="Bill to" hint={`E.g. the company or parent paying. Empty: ${student?.name || "the student"}.`}>
          <Input
            value={form.name}
            maxLength={LIMITS.name}
            placeholder={student?.name || ""}
            onChange={(e) => set({ name: e.target.value })}
          />
        </FormField>
        <FormField label="Billing address" hint={student?.address ? "Empty: the student's address." : undefined}>
          <Textarea
            rows={2}
            value={form.address}
            maxLength={LIMITS.address}
            placeholder={student?.address || ""}
            onChange={(e) => set({ address: e.target.value })}
          />
        </FormField>

        {gst && (
          <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
            <FormField
              label="Buyer's GSTIN"
              error={gstin ? gstinError : null}
              hint={gstinWarning
                ? "Double-check this GSTIN — its last character doesn't match the rest."
                : "Only if they are GST-registered and want to claim the input tax."}
            >
              <Input
                value={form.gstin}
                maxLength={20}
                autoComplete="off"
                placeholder="27AAACR5055K1Z7"
                onChange={(e) => set({ gstin: e.target.value.toUpperCase() })}
              />
            </FormField>
            <FormField label="Place of supply" hint="Where the buyer is. Another state is charged IGST.">
              <NativeSelect
                value={form.stateCode}
                onChange={(e) => set({ stateCode: e.target.value })}
                options={[
                  { value: "", label: impliedState ? `${stateLabel(impliedState)}${gstin && !gstinError ? " — from the GSTIN" : " — your state"}` : "Your state" },
                  ...STATE_OPTIONS,
                ]}
              />
            </FormField>
          </div>
        )}

        {gst && (
          <Callout tone="info">
            {interState
              ? `Charged as IGST @ ${gst.rate}% — the buyer is in ${chosenState.name}.`
              : `Charged as CGST + SGST (${gst.rate / 2}% each)${chosenState ? ` — the buyer is in ${chosenState.name}` : ""}.`}
          </Callout>
        )}
      </form>
    </AppModal>
  );
}
